/**
 * MODAL: ASSIGN CREW
 * Modale simple pour assigner un équipage avec recherche
 */

import { useState, useMemo, useEffect } from 'react';
import { Search } from 'lucide-react';
import { Modal } from '../ui/modal';
import { Badge } from '../ui';
import { useSupabase } from '@/hooks/useSupabase';
import type { Crew } from '@/types/crew';
import { CREW_STATUS_LABELS, CREW_STATUS_COLORS, CREW_PROBABILITY_LABELS } from '@/types/crew';

const PAGE_SIZE = 1000;

interface AssignCrewModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentCrewId?: string | null;
  onAssign: (crewId: string) => Promise<void>;
  excludeCrewIds?: string[];
}

export function AssignCrewModal({
  isOpen,
  onClose,
  currentCrewId,
  onAssign,
  excludeCrewIds = [],
}: AssignCrewModalProps) {
  const supabase = useSupabase();
  const [crews, setCrews] = useState<Crew[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);

  // Charger TOUS les équipages avec pagination (PostgREST limite à 1000 par requête)
  useEffect(() => {
    if (!isOpen) return;

    async function loadAllCrews() {
      setLoading(true);
      setLoadError(null);
      try {
        let allCrews: any[] = [];
        let from = 0;

        while (true) {
          const { data, error } = await supabase
            .from('crews')
            .select(
              `
              *,
              product:products(id, name),
              members:crew_members(
                contact:contacts(id)
              )
            `
            )
            .order('created_at', { ascending: false })
            .range(from, from + PAGE_SIZE - 1);

          if (error) {
            console.error('[AssignCrewModal] loadAllCrews error:', error);
            setLoadError(`Erreur: ${error.message}`);
            setCrews([]);
            return;
          }

          if (!data || data.length === 0) break;
          allCrews = [...allCrews, ...data];
          if (data.length < PAGE_SIZE) break;
          from += PAGE_SIZE;
        }

        setCrews(allCrews as any[]);
      } catch (err) {
        console.error('[AssignCrewModal] loadAllCrews exception:', err);
        setLoadError('Erreur lors du chargement des équipages');
        setCrews([]);
      } finally {
        setLoading(false);
      }
    }

    loadAllCrews();
  }, [isOpen, supabase]);

  // Filtrer les équipages
  const filteredCrews = useMemo(() => {
    let filtered = crews
      .filter((crew) => !excludeCrewIds.includes(crew.id))
      // Exclure les équipages qui ont déjà 2 membres
      .filter((crew) => !crew.members || crew.members.length < 2);

    if (searchTerm.trim()) {
      const normalize = (s: string) =>
        s
          .normalize('NFD')
          .replace(/\p{Diacritic}/gu, '')
          .toLowerCase();
      const term = normalize(searchTerm);
      filtered = filtered.filter((crew) => {
        const displayName = normalize(
          crew.name || (crew.crew_number ? `Équipage ${crew.crew_number}` : 'Équipage sans nom')
        );
        return displayName.includes(term) || normalize(crew.crew_number || '').includes(term);
      });
    }

    return filtered;
  }, [crews, searchTerm, excludeCrewIds]);

  async function handleAssign(crewId: string) {
    setIsAssigning(true);
    try {
      await onAssign(crewId);
      onClose();
      setSearchTerm('');
    } finally {
      setIsAssigning(false);
    }
  }

  function handleClose() {
    setSearchTerm('');
    setLoadError(null);
    onClose();
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Assigner un équipage" size="md">
      {/* Champ de recherche */}
      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-zinc-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Rechercher un équipage..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-zinc-300 rounded-md focus:outline-none focus:ring-2 focus:ring-zinc-900"
            autoFocus
          />
        </div>
      </div>

      {/* Liste des équipages */}
      {loading ? (
        <div className="text-center py-8 text-sm text-zinc-500">Chargement...</div>
      ) : loadError ? (
        <div className="text-center py-8 text-sm text-red-500">{loadError}</div>
      ) : filteredCrews.length > 0 ? (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {filteredCrews.map((crew) => (
            <button
              key={crew.id}
              onClick={() => handleAssign(crew.id)}
              disabled={isAssigning || crew.id === currentCrewId}
              className="w-full text-left p-3 rounded-lg border border-zinc-200 hover:bg-zinc-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-zinc-900 truncate">
                    {crew.name ||
                      (crew.crew_number ? `Équipage ${crew.crew_number}` : 'Équipage sans nom')}
                  </div>
                  {crew.crew_number && crew.name && (
                    <div className="text-sm text-zinc-500 mt-0.5">N° {crew.crew_number}</div>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Badge
                    className={`text-xs ${CREW_STATUS_COLORS[crew.status].bg} ${CREW_STATUS_COLORS[crew.status].text} border ${CREW_STATUS_COLORS[crew.status].border}`}
                  >
                    {CREW_STATUS_LABELS[crew.status]}
                  </Badge>
                  <Badge variant="secondary" className="text-xs">
                    {CREW_PROBABILITY_LABELS[crew.probability]}
                  </Badge>
                </div>
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-sm text-zinc-500">
          {searchTerm.trim()
            ? 'Aucun équipage trouvé'
            : 'Aucun équipage disponible (les équipages avec 2 membres sont exclus)'}
        </div>
      )}
    </Modal>
  );
}
